import { create } from "zustand";
import { initialAssets } from "@/data/assets";
import type { Asset } from "@/types/asset";
import type { SceneObject, SceneTransform } from "@/types/scene";
import { createUniqueObjectId } from "@/utils/sceneUtils";

const HISTORY_LIMIT = 50;
const cloneObjects = (objects: SceneObject[]): SceneObject[] => structuredClone(objects);
const defaultTransform = (): SceneTransform => ({
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },
});

interface SceneState {
  objects: SceneObject[];
  assets: Asset[];
  past: SceneObject[][];
  future: SceneObject[][];
  transformStart: SceneObject[] | null;
  revision: number;
  addFromAsset: (assetId: string) => string | null;
  addUploadedAsset: (asset: Asset) => void;
  updateTransform: (id: string, transform: SceneTransform, record?: boolean) => void;
  updateCameraConfig: (id: string, config: NonNullable<SceneObject["camera"]>) => void;
  beginTransform: () => void;
  commitTransform: () => void;
  renameObject: (id: string, name: string) => void;
  duplicateObject: (id: string) => string | null;
  removeObject: (id: string) => void;
  replaceObjects: (objects: SceneObject[], resetHistory?: boolean) => void;
  clearScene: () => void;
  undo: () => void;
  redo: () => void;
}

const commit = (state: SceneState, objects: SceneObject[]) => ({
  objects,
  past: [...state.past, cloneObjects(state.objects)].slice(-HISTORY_LIMIT),
  future: [],
  revision: state.revision + 1,
});

export const useSceneStore = create<SceneState>((set, get) => ({
  objects: [],
  assets: initialAssets,
  past: [],
  future: [],
  transformStart: null,
  revision: 0,
  addFromAsset: (assetId) => {
    const state = get();
    const asset = state.assets.find((item) => item.id === assetId);
    if (!asset) return null;
    const id = createUniqueObjectId(asset, state.objects);
    const count = state.objects.filter((object) => object.assetId === asset.id).length + 1;
    const sceneObject: SceneObject = {
      id,
      name: `${asset.name} ${count}`,
      assetId: asset.id,
      type: asset.type,
      modelUrl: asset.modelUrl,
      primitive: asset.primitive,
      transform: defaultTransform(),
      visible: true,
      interactionPoint: asset.interactionPoint,
      camera: asset.type === "camera" ? { fieldOfView: 50, near: .1, far: 300 } : undefined,
    };
    set(commit(state, [...state.objects, sceneObject]));
    return id;
  },
  addUploadedAsset: (asset) => set((state) => ({ assets: [...state.assets, asset] })),
  updateTransform: (id, transform, record = true) => set((state) => {
    const objects = state.objects.map((object) => object.id === id ? { ...object, transform } : object);
    return record ? commit(state, objects) : { objects };
  }),
  updateCameraConfig: (id, camera) => set((state) => commit(state, state.objects.map((object) => object.id === id ? { ...object, camera } : object))),
  beginTransform: () => set((state) => ({ transformStart: cloneObjects(state.objects) })),
  commitTransform: () => set((state) => state.transformStart ? ({
    past: [...state.past, state.transformStart].slice(-HISTORY_LIMIT),
    future: [],
    transformStart: null,
    revision: state.revision + 1,
  }) : {}),
  renameObject: (id, name) => set((state) => commit(state, state.objects.map((object) => object.id === id ? { ...object, name: name.trim() || object.name } : object))),
  duplicateObject: (id) => {
    const state = get();
    const source = state.objects.find((object) => object.id === id);
    const asset = state.assets.find((item) => item.id === source?.assetId);
    if (!source || !asset) return null;
    const duplicateId = createUniqueObjectId(asset, state.objects);
    const duplicate: SceneObject = cloneObjects([source])[0];
    duplicate.id = duplicateId;
    duplicate.name = `${source.name.replace(/ copy( \d+)?$/i, "")} copy`;
    duplicate.transform.position.x += .75;
    duplicate.transform.position.z += .75;
    set(commit(state, [...state.objects, duplicate]));
    return duplicateId;
  },
  removeObject: (id) => set((state) => commit(state, state.objects.filter((object) => object.id !== id))),
  replaceObjects: (objects, resetHistory = true) => set((state) => ({
    objects: cloneObjects(objects),
    past: resetHistory ? [] : [...state.past, cloneObjects(state.objects)].slice(-HISTORY_LIMIT),
    future: [],
    revision: state.revision + 1,
  })),
  clearScene: () => set((state) => commit(state, [])),
  undo: () => set((state) => {
    const previous = state.past.at(-1);
    if (!previous) return {};
    return { objects: cloneObjects(previous), past: state.past.slice(0, -1), future: [cloneObjects(state.objects), ...state.future], revision: state.revision + 1 };
  }),
  redo: () => set((state) => {
    const next = state.future[0];
    if (!next) return {};
    return { objects: cloneObjects(next), past: [...state.past, cloneObjects(state.objects)].slice(-HISTORY_LIMIT), future: state.future.slice(1), revision: state.revision + 1 };
  }),
}));
